import { DataDivisionType, LayerInfo } from "src/common/managers/Types";
import { Mat_Jordstykke, Mat_SamletFastEjendom } from "src/dataCollector/DFQGLTypes";
import { DataCollector } from "src/dataCollector/DataCollector";
import { GenerateGeomUtils } from "src/managers/GenerateGeomUtils";
import { PropertyInformation } from "src/managers/PropertyInformation";
import chroma from "chroma-js";

export type SFEAndColor = { sfe: number, color: string };
export type MatrikelRapportSFE = Mat_SamletFastEjendom & { adresse?: string };

export type MatrikelEjerlavGroup = {
   matrikelNumbers: string[];
   ejerlavName: string;
   ejerlavKode: string;
};

export type EjendomskortTitleBlockData = {
   matrikelEjerlavGroups: MatrikelEjerlavGroup[];
   matrikelGroups: string[][];
   ejerlavNames: string[];
   kommune: string;
   region: string;
   samletAreal: number;
   vejareal: number;
   sfeNumre: number[];
   matrikelCount: number;
   propertyCount: number;
}

export function SFEAndColorList(layerInfoArr: LayerInfo[]): SFEAndColor[] {
   let list: SFEAndColor[] = []
   let sfeList = new Set<number>();
   let validLayers = layerInfoArr.filter(layer => layer.propertiesInGeoJson?.includes("SFEnummer"));

   validLayers.forEach(layer => {
      let getColor = (_p) => layer.styling.color && chroma(layer.styling.color).hex("rgb") || "black";
      if (layer.styling.colorByProperty) {
         if (layer.styling.colorByValue?.divisions?.type == DataDivisionType.Discrete) {
            let style = layer.styling
            getColor = (properties) => {
               if (style.colorByProperty) {
                  let x = GenerateGeomUtils.getColorByValueDiscrete(undefined as any, properties[style.colorByProperty], style.colorByValue?.divisions, style.outsideColorRange);
                  return x.color && chroma(x.color).hex("rgb") || "black";
               }
               return "black";
            }
         } else if (layer.styling.colorByValue?.divisions?.type == DataDivisionType.Continous) {
            let style = layer.styling
            getColor = (properties) => {
               if (style.colorByProperty) {
                  let x = GenerateGeomUtils.getColorByValueNummeric(undefined as any, properties[style.colorByProperty], style.colorByValue?.divisions, style.outsideColorRange);
                  return x.color && chroma(x.color).hex("rgb") || "black";
               }
               return "black";
            }
         }
      }
      layer.geoJson?.features?.forEach(feature => {
         let sfe = feature.properties?.SFEnummer;
         if (sfe && typeof sfe === "string") {
            sfe = parseInt(sfe)
         }
         if (sfe && !sfeList.has(sfe)) {
            sfeList.add(sfe);

            list.push({ sfe: sfe, color: getColor(feature.properties) });
         }
      });
   });
   return list;
}

export function EBRToActualAdresse(ebr: any): string {
   const node = ebr?.EBR_Ejendomsbeliggenhed?.nodes?.find((n: any) => n.status === "gældende") ?? ebr?.EBR_Ejendomsbeliggenhed?.nodes?.[0];
   if (!node) return "Ukendt adresse";
   if (node.betegnelse) {
      return node.betegnelse as string;
   }
   const husnummer = node.ejendomsbeliggenhedHarSomBeliggenhedsadresseHusnummer;
   if (husnummer?.adgangsadressebetegnelse) {
      return husnummer.adgangsadressebetegnelse as string;
   }
   return "Ukendt adresse";
}

export async function hentDataOgBelligenhed(liste: { sfe: number }[]): Promise<MatrikelRapportSFE[]> {
   return (await Promise.all(liste.map(async (a) => {
      const [matrikelData, beliggenhedData] = await Promise.all([
         DataCollector.DF_GQL.Mat2.BestemtFastEjendom(a.sfe.toString()),
         DataCollector.DF_GQL.EBR.EjendomsbeliggenhedForBFE(a.sfe + "")
      ]);
      const node = matrikelData.MAT_SamletFastEjendom?.nodes?.find((n: any) => n.status === "Gældende") as Mat_SamletFastEjendom | undefined;
      return node ? ({ ...node, adresse: EBRToActualAdresse(beliggenhedData) } as MatrikelRapportSFE) : null;
   }))).filter((n): n is MatrikelRapportSFE => n !== null);
}

export function matrikelSort(a: Mat_Jordstykke, b: Mat_Jordstykke) {
   let parseEjerlav =
      parseInt(a.jordstykkeLiggerIEjerlav?.ejerlavskode || "0", 10) -
      parseInt(b.jordstykkeLiggerIEjerlav?.ejerlavskode || "0", 10);
   if (parseEjerlav !== 0) {
      return parseEjerlav;
   }
   return PropertyInformation.matrikelSortFunc(
      a.matrikelnummer || "",
      b.matrikelnummer || ""
   );
}

export function getGaeldendeJordstykker(sfe: MatrikelRapportSFE): Mat_Jordstykke[] {
   return (sfe.jordstykkeSamlesISamletFastEjendom?.nodes || [])
      .filter((jordstykke): jordstykke is Mat_Jordstykke => Boolean(jordstykke) && jordstykke.status == "Gældende")
      .toSorted(matrikelSort);
}

function uniqueText(values: (string | undefined | null)[]) {
   return [...new Set(values.filter((a): a is string => Boolean(a)))].join(", ");
}

function ejerlavMatrikelGroups(jordstykker: Mat_Jordstykke[]): MatrikelEjerlavGroup[] {
   const sorted = jordstykker.toSorted(matrikelSort);
   const groups: MatrikelEjerlavGroup[] = [];
   let currentEjerlavKode: string | undefined;

   sorted.forEach((jordstykke) => {
      const matrikel = jordstykke.matrikelnummer?.trim();
      if (!matrikel) {
         return;
      }

      const ejerlavKode = jordstykke.jordstykkeLiggerIEjerlav?.ejerlavskode || "";
      const ejerlavName = jordstykke.jordstykkeLiggerIEjerlav?.ejerlavsnavn?.trim() || "Ukendt ejerlav";
      const lastGroup = groups[groups.length - 1];

      if (lastGroup && ejerlavKode === currentEjerlavKode) {
         lastGroup.matrikelNumbers.push(matrikel);
         return;
      }

      currentEjerlavKode = ejerlavKode;
      groups.push({ ejerlavName, ejerlavKode, matrikelNumbers: [matrikel] });
   });

   return groups;
}

export function formatSemicolonList(values: string[], emptyFallback: string): string {
   if (!values.length) {
      return emptyFallback;
   }
   return values.join("; ");
}

export function formatMatrikelGroups(groups: string[][], emptyFallback: string): string {
   const nonEmptyGroups = groups.filter((group) => group.length);
   if (!nonEmptyGroups.length) {
      return emptyFallback;
   }
   return nonEmptyGroups.map((group) => group.join(", ")).join("; ");
}

export function formatMatrikelEjerlavLine(groups: MatrikelEjerlavGroup[], emptyFallback: string): string {
   if (!groups.length) {
      return emptyFallback;
   }
   return groups.map((group) => {
      const matrikler = group.matrikelNumbers.join(", ");
      const ejerlavPart = group.ejerlavKode
         ? `${group.ejerlavName} (${group.ejerlavKode})`
         : group.ejerlavName;
      return `${matrikler}  ${ejerlavPart}`;
   }).join(", ");
}

export function makeEjendomskortTitleBlockData(data: MatrikelRapportSFE[]): EjendomskortTitleBlockData | null {
   if (!data.length) {
      return null;
   }

   const jordstykker = data.flatMap((sfe) => getGaeldendeJordstykker(sfe));
   const groups = ejerlavMatrikelGroups(jordstykker);
   return {
      matrikelEjerlavGroups: groups,
      matrikelGroups: groups.map((group) => group.matrikelNumbers),
      ejerlavNames: groups.map((group) => group.ejerlavName),
      kommune: uniqueText(jordstykker.map((jordstykke) => jordstykke.jordstykkeLiggerIMatrikelKommune?.kommunenavn)),
      region: uniqueText(jordstykker.map((jordstykke) => jordstykke.jordstykkeLiggerIMatrikelRegion?.regionsnavn)),
      samletAreal: jordstykker.reduce((sum, jordstykke) => sum + (jordstykke.registreretAreal || 0), 0),
      vejareal: jordstykker.reduce((sum, jordstykke) => sum + (jordstykke.vejareal || 0), 0),
      sfeNumre: data.map((sfe) => Number(sfe.BFEnummer)).filter(Boolean),
      matrikelCount: jordstykker.length,
      propertyCount: data.length,
   };
}
